import { useState } from "react";
import { formService } from "../service";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";

// Unified backend-supported field types
export const FIELD_OPTIONS = [
  { label: "Identifiant unique", value: "id" },
  { label: "Texte court (string)", value: "string" },
  { label: "Texte long (textarea)", value: "text" },
  { label: "Nombre entier", value: "integer" },
  { label: "Nombre décimal", value: "float" },
  { label: "Booléen (Oui/Non)", value: "boolean" },
  { label: "Titre de section", value: "section-title" },
  { label: "Choix multiple", value: "checkbox" },
  { label: "Choix unique", value: "radio" },
  { label: "Date", value: "date" },
];

export const useEditForm = (form, onBack) => {
  const [formTitle, setFormTitle] = useState(form?.title || "");
  const [formDescription, setFormDescription] = useState(form?.description || "");
  const [fields, setFields] = useState(form?.fields || []);
  const [optionInput, setOptionInput] = useState({});

  // ---------------------------
  // CRUD helpers
  // ---------------------------

  const addField = () =>
    setFields((prev) => [
      ...prev,
      {
        id: Date.now(),
        label: "",
        field_type: "",
        required: false,
        conditions: {},
      },
    ]);

  const deleteField = (id) =>
    setFields((prev) => prev.filter((f) => f.id !== id));

  const updateField = (id, key, value) =>
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: value } : f))
    );

  const updateCondition = (field, key, value) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === field.id
          ? { ...f, conditions: { ...f.conditions, [key]: value } }
          : f
      )
    );
  };

  // ---------------------------
  // Option helpers
  // ---------------------------

  const addOption = (fieldId, value) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? {
              ...f,
              conditions: {
                ...f.conditions,
                options: [...(f.conditions.options || []), value],
              },
            }
          : f
      )
    );
  };

  const removeOption = (fieldId, value) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? {
              ...f,
              conditions: {
                ...f.conditions,
                options: (f.conditions.options || []).filter(
                  (opt) => opt !== value
                ),
              },
            }
          : f
      )
    );
  };

  // ---------------------------
  // Normalization
  // ---------------------------

  const normalizeField = (field) => {
    const base = {
      label: field.label.trim(),
      field_type: field.field_type,
      required: field.required,
      conditions: {},
    };

    switch (field.field_type) {
      case "id":
        base.conditions = {
          auto_generated: field.conditions?.auto_generated ?? true,
        };
        break;
      case "integer":
      case "float":
      case "date":
        base.conditions = {
          min: field.conditions?.min || null,
          max: field.conditions?.max || null,
        };
        break;
      case "checkbox":
      case "radio":
        base.conditions = {
          options: field.conditions?.options?.filter((o) => o.trim()) || [],
        };
        break;
      default:
        base.conditions = field.conditions || {};
    }

    return base;
  };

  // ---------------------------
  // Save form
  // ---------------------------

  const handleSave = async () => {
    try {
      if (!formTitle.trim()) {
        showErrorToast("Le titre du formulaire est obligatoire.");
        return;
      }

      const validFields = fields
        .map(normalizeField)
        .filter(
          (f) =>
            f.label.trim() !== "" &&
            f.field_type &&
            f.field_type.trim() !== ""
        );

      const payload = {
        title: formTitle.trim(),
        description: formDescription.trim(),
        fields: validFields,
      };

      await formService.update(form.id, payload);
      showSuccessToast("Formulaire mis à jour avec succès !");
      onBack(true);
    } catch (err) {
      console.error("Erreur lors de la mise à jour du formulaire :", err);
      showErrorToast("Erreur lors de la mise à jour du formulaire.");
    }
  };

  return {
    formTitle,
    setFormTitle,
    formDescription,
    setFormDescription,
    fields,
    setFields,
    optionInput,
    setOptionInput,
    addField,
    deleteField,
    updateField,
    updateCondition,
    addOption,
    removeOption,
    handleSave,
  };
};
